import React, { useState, useEffect, useCallback } from 'react';
import { api, useAuth } from '../App';
import Modal from '../components/UI/Modal';
import StatusPill from '../components/UI/StatusPill';
import {
  Plus, Filter, CheckSquare, Clock, AlertTriangle, Shield,
  Calendar, MapPin, User, X, ChevronDown, RefreshCw, Eye,
  Play, Check, Edit2, Trash2, ListChecks
} from 'lucide-react';

const PriorityBadge = ({ priority }) => {
  const colors = { urgent: '#E74C3C', high: '#F39C12', medium: '#78A8E8', low: '#4A6A8A' };
  return <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: (colors[priority] || '#4A6A8A') + '1A', color: colors[priority] || '#4A6A8A', textTransform: 'uppercase' }}>{priority}</span>;
};

const checklistProgress = (items) => {
  if (!items || !items.length) return null;
  const list = typeof items === 'string' ? JSON.parse(items) : items;
  const total = list.length;
  const done = list.filter(i => i.checked).length;
  return { done, total, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
};

const emptyForm = {
  title: '', description: '', task_type: 'one_time', priority: 'medium',
  location_id: '', assigned_user_id: '', assigned_role: '',
  is_compliance: false, due_date: '', due_time: '',
  checklist_items: []
};

export default function Tasks() {
  const { user } = useAuth();

  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({ due_today: 0, overdue: 0, compliance_due: 0, completed_today: 0 });
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [locFilter, setLocFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [complianceOnly, setComplianceOnly] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [completionNotes, setCompletionNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadTasks = useCallback(async () => {
    const params = new URLSearchParams();
    if (statusFilter) params.append('status', statusFilter);
    if (locFilter) params.append('location_id', locFilter);
    if (userFilter) params.append('assigned_user_id', userFilter);
    if (typeFilter) params.append('task_type', typeFilter);
    if (complianceOnly) params.append('is_compliance', '1');
    const qs = params.toString();

    try {
      const [taskRes, sumRes] = await Promise.all([
        api('/api/tasks' + (qs ? '?' + qs : '')).catch(() => ({ data: [] })),
        api('/api/tasks/summary' + (locFilter ? '?location_id=' + locFilter : '')).catch(() => ({ data: {} })),
      ]);
      setTasks(taskRes.data || []);
      setSummary(sumRes.data || { due_today: 0, overdue: 0, compliance_due: 0, completed_today: 0 });
    } catch (e) {
      console.error('Failed to load tasks', e);
    }
    setLoading(false);
  }, [statusFilter, locFilter, userFilter, typeFilter, complianceOnly]);

  useEffect(() => {
    Promise.all([
      api('/api/locations').catch(() => ({ data: [] })),
      api('/api/users').catch(() => ({ data: [] })),
      api('/api/tasks/templates/list').catch(() => ({ data: [] })),
    ]).then(([locRes, userRes, tplRes]) => {
      setLocations(locRes.data || []);
      setUsers(userRes.data || []);
      setTemplates(tplRes.data || []);
    });
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const openCreate = () => {
    setForm({ ...emptyForm });
    setEditId(null);
    setShowCreate(true);
  };

  const openEdit = (task) => {
    const items = task.checklist_items ? (typeof task.checklist_items === 'string' ? JSON.parse(task.checklist_items) : task.checklist_items) : [];
    setForm({
      title: task.title || '',
      description: task.description || '',
      task_type: task.task_type || 'one_time',
      priority: task.priority || 'medium',
      location_id: task.location_id || '',
      assigned_user_id: task.assigned_user_id || '',
      assigned_role: task.assigned_role || '',
      is_compliance: !!task.is_compliance,
      due_date: task.due_date ? task.due_date.slice(0, 10) : '',
      due_time: task.due_time ? String(task.due_time).slice(0, 5) : '',
      checklist_items: items,
    });
    setEditId(task.id);
    setShowCreate(true);
  };

  const saveTask = async () => {
    if (!form.title.trim()) return alert('Title is required');
    setSaving(true);
    try {
      const body = {
        ...form,
        checklist_items: form.checklist_items.length > 0 ? form.checklist_items : undefined,
      };
      if (editId) {
        await api('/api/tasks/' + editId, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await api('/api/tasks', { method: 'POST', body: JSON.stringify(body) });
      }
      setShowCreate(false);
      setEditId(null);
      loadTasks();
    } catch (e) { alert(e.error || 'Failed to save task'); }
    setSaving(false);
  };

  const createFromTemplate = async (templateId) => {
    if (!templateId) return;
    try {
      const res = await api('/api/tasks/from-template/' + templateId, { method: 'POST', body: JSON.stringify({ location_id: form.location_id || undefined }) });
      setShowCreate(false);
      loadTasks();
    } catch (e) { alert(e.error || 'Failed to create from template'); }
  };

  const startTask = async (id) => {
    try {
      await api('/api/tasks/' + id + '/start', { method: 'PUT' });
      loadTasks();
      if (detail?.id === id) {
        const res = await api('/api/tasks/' + id).catch(() => null);
        if (res?.data) setDetail(res.data);
      }
    } catch (e) { alert(e.error || 'Failed'); }
  };

  const completeTask = async (id) => {
    try {
      const task = tasks.find(t => t.id === id) || detail;
      const checklist = task?.checklist_items ? (typeof task.checklist_items === 'string' ? JSON.parse(task.checklist_items) : task.checklist_items) : null;
      await api('/api/tasks/' + id + '/complete', {
        method: 'PUT',
        body: JSON.stringify({ completion_notes: completionNotes, checklist_items: checklist })
      });
      setDetail(null);
      setCompletionNotes('');
      loadTasks();
    } catch (e) { alert(e.error || 'Failed'); }
  };

  const cancelTask = async (id) => {
    if (!confirm('Cancel this task?')) return;
    try {
      await api('/api/tasks/' + id, { method: 'DELETE' });
      setDetail(null);
      loadTasks();
    } catch (e) { alert(e.error || 'Failed'); }
  };

  const toggleChecklistItem = async (taskId, idx) => {
    const task = tasks.find(t => t.id === taskId) || detail;
    if (!task) return;
    const items = typeof task.checklist_items === 'string' ? JSON.parse(task.checklist_items) : [...(task.checklist_items || [])];
    items[idx] = { ...items[idx], checked: !items[idx].checked, checked_at: new Date().toISOString(), checked_by: user?.name };
    try {
      await api('/api/tasks/' + taskId + '/checklist', { method: 'PUT', body: JSON.stringify({ checklist_items: items }) });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, checklist_items: items } : t));
      if (detail?.id === taskId) setDetail({ ...detail, checklist_items: items });
    } catch (e) { console.error('Failed to update checklist'); }
  };

  const addChecklistItem = () => {
    setForm(f => ({ ...f, checklist_items: [...f.checklist_items, { label: '', required: false, checked: false }] }));
  };

  const removeChecklistItem = (idx) => {
    setForm(f => ({ ...f, checklist_items: f.checklist_items.filter((_, i) => i !== idx) }));
  };

  const updateChecklistItem = (idx, field, value) => {
    setForm(f => ({
      ...f,
      checklist_items: f.checklist_items.map((item, i) => i === idx ? { ...item, [field]: value } : item)
    }));
  };

  const statusTabs = [
    { value: '', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const openTotal = (summary.due_today || 0) + (summary.overdue || 0);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200 }}>
      {/* ── HEADER ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Lora, serif', fontSize: 26 }}>Task Management</h1>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> Create Task</button>
      </div>

      {/* ── FILTER BAR ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20, alignItems: 'center' }}>
        {/* Status tabs */}
        <div style={{ display: 'flex', gap: 2, background: 'var(--card)', borderRadius: 8, padding: 3 }}>
          {statusTabs.map(tab => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              style={{
                padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                background: statusFilter === tab.value ? 'var(--gold)' : 'transparent',
                color: statusFilter === tab.value ? 'var(--navy)' : 'var(--muted)',
                transition: 'all 0.15s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <select value={locFilter} onChange={e => setLocFilter(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 13 }}>
          <option value="">All Locations</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>

        <select value={userFilter} onChange={e => setUserFilter(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 13 }}>
          <option value="">All Employees</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>

        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 13 }}>
          <option value="">All Types</option>
          <option value="one_time">One-time</option>
          <option value="recurring">Recurring</option>
          <option value="compliance">Compliance</option>
          <option value="workflow">Workflow</option>
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>
          <input type="checkbox" checked={complianceOnly} onChange={e => setComplianceOnly(e.target.checked)} />
          Compliance only
        </label>
      </div>

      {/* ── SUMMARY CARDS ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        <SumCard label="Total Open" value={openTotal} color="var(--c-co2)" />
        <SumCard label="Overdue" value={summary.overdue || 0} color="var(--danger)" urgent={summary.overdue > 0} />
        <SumCard label="Due Today" value={summary.due_today || 0} color="var(--warning)" urgent={summary.due_today > 0} />
        <SumCard label="Completed Today" value={summary.completed_today || 0} color="var(--success)" />
        <SumCard label="Compliance Due" value={summary.compliance_due || 0} color="var(--warning)" urgent={summary.compliance_due > 0} />
      </div>

      {/* ── TASK TABLE ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>
          <RefreshCw size={24} className="spin" style={{ color: 'var(--gold)' }} />
        </div>
      ) : tasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--muted)' }}>
          <ListChecks size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <div style={{ fontSize: 15 }}>No tasks found</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Create a new task or adjust your filters.</div>
        </div>
      ) : (
        <div style={{ background: 'var(--card)', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--navy2)' }}>
                <th style={thStyle}>Title</th>
                <th style={thStyle}>Assigned To</th>
                <th style={thStyle}>Location</th>
                <th style={thStyle}>Due</th>
                <th style={thStyle}>Priority</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Checklist</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task, ri) => {
                const isOverdue = task.status === 'overdue' || (task.due_date && task.status !== 'completed' && task.status !== 'cancelled' && new Date(task.due_date) < new Date(new Date().toDateString()));
                const progress = checklistProgress(task.checklist_items);
                return (
                  <tr key={task.id} style={{ background: ri % 2 === 0 ? 'var(--card2)' : 'var(--card)', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-row)'}
                    onMouseLeave={e => e.currentTarget.style.background = ri % 2 === 0 ? 'var(--card2)' : 'var(--card)'}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, fontSize: 13, cursor: 'pointer', color: 'var(--text)' }} onClick={() => setDetail(task)}>
                        {task.title}
                      </div>
                      <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                        {task.is_compliance && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(243,156,18,.15)', color: 'var(--warning)', fontWeight: 700 }}>COMPLIANCE</span>}
                        {task.task_type && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'var(--navy2)', color: 'var(--muted)', textTransform: 'capitalize' }}>{task.task_type.replace('_', ' ')}</span>}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 13 }}>{task.assigned_user_name || task.assigned_role || '-'}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 13, color: 'var(--muted)' }}>{task.location_name || '-'}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 12, color: isOverdue ? 'var(--danger)' : 'var(--muted)', fontWeight: isOverdue ? 700 : 400 }}>
                        {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                        {task.due_time ? <><br />{String(task.due_time).slice(0, 5)}</> : ''}
                      </span>
                    </td>
                    <td style={tdStyle}><PriorityBadge priority={task.priority} /></td>
                    <td style={tdStyle}><StatusPill status={task.status} /></td>
                    <td style={tdStyle}>
                      {progress ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 12, color: progress.done === progress.total ? 'var(--success)' : 'var(--muted)', fontWeight: 600 }}>{progress.done}/{progress.total}</span>
                          <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--navy)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: progress.pct + '%', background: progress.done === progress.total ? 'var(--success)' : 'var(--gold)', borderRadius: 2 }} />
                          </div>
                        </div>
                      ) : <span style={{ fontSize: 12, color: 'var(--faint)' }}>-</span>}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => setDetail(task)} title="View"><Eye size={13} /></button>
                        {task.status !== 'completed' && task.status !== 'cancelled' && (
                          <>
                            <button className="btn btn-primary btn-sm" onClick={() => completeTask(task.id)} title="Complete"><Check size={13} /></button>
                            <button className="btn btn-secondary btn-sm" onClick={() => openEdit(task)} title="Edit"><Edit2 size={13} /></button>
                            <button className="btn btn-danger btn-sm" onClick={() => cancelTask(task.id)} title="Cancel"><X size={13} /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── CREATE/EDIT MODAL ── */}
      {showCreate && (
        <Modal title={editId ? 'Edit Task' : 'Create Task'} onClose={() => { setShowCreate(false); setEditId(null); }}>
          {/* From Template */}
          {!editId && templates.length > 0 && (
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label>From Template</label>
              <select onChange={e => createFromTemplate(e.target.value)} defaultValue=""
                style={{ fontSize: 14 }}>
                <option value="" disabled>Select a template...</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name || t.title}</option>)}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Task title" autoFocus />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Task description..." />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Task Type</label>
              <select value={form.task_type} onChange={e => setForm(f => ({ ...f, task_type: e.target.value }))}>
                <option value="one_time">One-time</option>
                <option value="recurring">Recurring</option>
                <option value="compliance">Compliance</option>
              </select>
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Location</label>
              <select value={form.location_id} onChange={e => setForm(f => ({ ...f, location_id: e.target.value }))}>
                <option value="">No location</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Assign To</label>
              <select value={form.assigned_user_id} onChange={e => setForm(f => ({ ...f, assigned_user_id: e.target.value }))}>
                <option value="">Unassigned</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Due Time</label>
              <input type="time" value={form.due_time} onChange={e => setForm(f => ({ ...f, due_time: e.target.value }))} />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.is_compliance} onChange={e => setForm(f => ({ ...f, is_compliance: e.target.checked }))} />
              <Shield size={14} style={{ color: 'var(--warning)' }} />
              Compliance Task
            </label>
          </div>

          {/* Checklist Builder */}
          <div style={{ marginTop: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <label style={{ fontWeight: 600, fontSize: 14 }}>Checklist Items</label>
              <button className="btn btn-secondary btn-sm" onClick={addChecklistItem}><Plus size={13} /> Add Item</button>
            </div>
            {form.checklist_items.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <input
                  value={item.label}
                  onChange={e => updateChecklistItem(idx, 'label', e.target.value)}
                  placeholder={'Checklist item ' + (idx + 1)}
                  style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 13 }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                  <input type="checkbox" checked={item.required} onChange={e => updateChecklistItem(idx, 'required', e.target.checked)} />
                  Req
                </label>
                <button onClick={() => removeChecklistItem(idx)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: 4 }}>
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => { setShowCreate(false); setEditId(null); }}>Cancel</button>
            <button className="btn btn-primary" onClick={saveTask} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update Task' : 'Create Task'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── TASK DETAIL MODAL ── */}
      {detail && (
        <Modal title={detail.title} onClose={() => { setDetail(null); setCompletionNotes(''); }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
            {detail.is_compliance && <span style={{ padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: 'rgba(243,156,18,.15)', color: 'var(--warning)' }}>COMPLIANCE</span>}
            <span style={{ padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'var(--card)', color: 'var(--muted)', textTransform: 'capitalize' }}>{detail.task_type?.replace('_', ' ')}</span>
            <PriorityBadge priority={detail.priority} />
            <StatusPill status={detail.status} />
          </div>

          {detail.description && <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>{detail.description}</p>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16, fontSize: 13 }}>
            {detail.location_name && <div><span style={{ color: 'var(--faint)' }}>Location:</span> <span style={{ fontWeight: 500 }}>{detail.location_name}</span></div>}
            {detail.due_date && <div><span style={{ color: 'var(--faint)' }}>Due:</span> <span style={{ fontWeight: 500, color: (detail.status === 'overdue' || (detail.status !== 'completed' && detail.status !== 'cancelled' && new Date(detail.due_date) < new Date(new Date().toDateString()))) ? 'var(--danger)' : undefined }}>{new Date(detail.due_date).toLocaleDateString()}{detail.due_time ? ' ' + String(detail.due_time).slice(0, 5) : ''}</span></div>}
            {detail.assigned_user_name && <div><span style={{ color: 'var(--faint)' }}>Assigned to:</span> <span style={{ fontWeight: 500 }}>{detail.assigned_user_name}</span></div>}
            {detail.created_by_name && <div><span style={{ color: 'var(--faint)' }}>Created by:</span> <span style={{ fontWeight: 500 }}>{detail.created_by_name}</span></div>}
          </div>

          {/* Checklist */}
          {(() => {
            const items = detail.checklist_items ? (typeof detail.checklist_items === 'string' ? JSON.parse(detail.checklist_items) : detail.checklist_items) : null;
            if (!items || !items.length) return null;
            const done = items.filter(i => i.checked).length;
            return (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Checklist</span>
                  <span style={{ fontSize: 12, color: done === items.length ? 'var(--success)' : 'var(--muted)' }}>{done}/{items.length}</span>
                </div>
                <div style={{ width: '100%', height: 4, borderRadius: 2, background: 'var(--navy)', marginBottom: 10, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: (done / items.length * 100) + '%', background: done === items.length ? 'var(--success)' : 'var(--gold)', borderRadius: 2, transition: 'width 0.3s' }} />
                </div>
                {items.map((item, idx) => (
                  <div key={idx} onClick={() => detail.status !== 'completed' && detail.status !== 'cancelled' && toggleChecklistItem(detail.id, idx)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--border)', cursor: (detail.status !== 'completed' && detail.status !== 'cancelled') ? 'pointer' : 'default', transition: 'background 0.15s' }}
                    onMouseEnter={e => { if (detail.status !== 'completed' && detail.status !== 'cancelled') e.currentTarget.style.background = 'var(--hover-row)'; }}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ width: 22, height: 22, borderRadius: 6, border: '2px solid ' + (item.checked ? 'var(--success)' : 'var(--faint)'), background: item.checked ? 'var(--success)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
                      {item.checked && <Check size={14} style={{ color: '#fff' }} />}
                    </div>
                    <span style={{ fontSize: 14, textDecoration: item.checked ? 'line-through' : 'none', color: item.checked ? 'var(--faint)' : 'var(--text)' }}>
                      {item.label}
                      {item.required && <span style={{ color: 'var(--danger)', marginLeft: 2 }}>*</span>}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Completion info */}
          {detail.status === 'completed' && (
            <div style={{ padding: '12px 14px', background: 'rgba(46,204,113,.08)', border: '1px solid rgba(46,204,113,.2)', borderRadius: 10, marginBottom: 16, fontSize: 13 }}>
              <div style={{ color: 'var(--success)', fontWeight: 600, marginBottom: 4 }}>Completed</div>
              {detail.completed_by_name && <div style={{ color: 'var(--muted)' }}>By: {detail.completed_by_name}</div>}
              {detail.completed_at && <div style={{ color: 'var(--muted)' }}>{new Date(detail.completed_at).toLocaleString()}</div>}
              {detail.completed_on_time !== undefined && detail.completed_on_time !== null && <div style={{ color: detail.completed_on_time ? 'var(--success)' : 'var(--danger)' }}>{detail.completed_on_time ? 'On time' : 'Late'}</div>}
              {detail.completion_notes && <div style={{ color: 'var(--muted)', marginTop: 6 }}>{detail.completion_notes}</div>}
            </div>
          )}

          {/* Actions */}
          {detail.status !== 'completed' && detail.status !== 'cancelled' && (
            <div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label>Completion Notes (optional)</label>
                <textarea value={completionNotes} onChange={e => setCompletionNotes(e.target.value)} rows={2} placeholder="Any notes about this task..." />
              </div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => { setDetail(null); setCompletionNotes(''); }}>Close</button>
                {detail.status === 'pending' && <button className="btn btn-secondary" onClick={() => startTask(detail.id)}><Play size={14} /> Start Task</button>}
                <button className="btn btn-primary" onClick={() => completeTask(detail.id)}><Check size={14} /> Complete Task</button>
              </div>
            </div>
          )}

          {(detail.status === 'completed' || detail.status === 'cancelled') && (
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => { setDetail(null); setCompletionNotes(''); }}>Close</button>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

/* ── Sub-components ── */

function SumCard({ label, value, color, urgent }) {
  return (
    <div style={{
      padding: '18px 16px', borderRadius: 12, background: 'var(--card)',
      border: '1px solid ' + (urgent ? color : 'var(--border)'),
      cursor: 'default', transition: 'transform 0.15s, box-shadow 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px var(--shadow)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ fontSize: 28, fontWeight: 700, color, fontFamily: 'Lora, serif' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

const thStyle = { padding: '10px 12px', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left', borderBottom: '1px solid var(--border)' };
const tdStyle = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid var(--border)' };
